import { useEffect } from "react";
import { CircularProgress, MenuItem, Stack, TextField, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { stockApi, type StockAvailabilityDto } from "@/api/endpoints";
import {
  NEAR_EXPIRY_DAYS, daysToExpiry, dispensable, byFefo, expiryLabel, expiryState, pickDefaultBatch,
} from "@/shared/batches";

/**
 * Pick a batch of one item in one warehouse. Loads the dispensable batches, defaults to FEFO, and
 * shows expiry the same way the billing counter does — so a nurse issuing to a ward sees exactly
 * what a pharmacist sees at the till.
 *
 * Expired batches stay visible but are never auto-selected, and selecting one is flagged: the
 * server refuses them outright, so the message here explains a rejection before it happens.
 */
export function BatchSelect({
  itemId, warehouseId, value, onChange, qty = 0, label = "Batch",
}: {
  itemId?: string | null;
  warehouseId?: string | null;
  value: string;
  onChange: (batchId: string, batch?: StockAvailabilityDto) => void;
  /** Line quantity, used to prefer a batch that can actually cover it. */
  qty?: number;
  label?: string;
}) {
  const enabled = !!itemId && !!warehouseId;
  const q = useQuery({
    queryKey: ["batch-select", itemId, warehouseId],
    queryFn: async () => {
      const rows = await stockApi.availability({ itemId: itemId!, warehouseId: warehouseId! });
      return rows.filter(b => b.availableQty > 0).sort(byFefo);
    },
    enabled,
    retry: false,
    staleTime: 30_000,
  });

  const batches = q.data ?? [];
  const selected = batches.find(b => b.batchId === value);
  const short = !!selected && qty > 0 && selected.availableQty < qty;
  const exp = expiryState(selected?.expiryDate);

  // Default to FEFO as soon as the list lands, so the common case needs no interaction.
  // Only when nothing is chosen yet — never overrides a deliberate pick.
  useEffect(() => {
    if (!enabled || value || batches.length === 0) return;
    const fefo = pickDefaultBatch(batches, qty || 1);
    if (fefo) onChange(fefo, batches.find(b => b.batchId === fefo));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, value, batches]);

  if (!enabled) {
    return <Typography variant="caption" color="text.secondary">Pick an item first</Typography>;
  }
  if (q.isFetching && batches.length === 0) {
    return (
      <Stack direction="row" spacing={1} alignItems="center">
        <CircularProgress size={14} />
        <Typography variant="caption" color="text.secondary">Loading batches…</Typography>
      </Stack>
    );
  }
  if (batches.length === 0) {
    return <Typography variant="caption" color="error">No stock in this warehouse</Typography>;
  }

  return (
    <Stack spacing={0.25}>
      <TextField
        select size="small" fullWidth label={label} value={value}
        onChange={(e) => onChange(e.target.value, batches.find(b => b.batchId === e.target.value))}
        error={!value || exp.color === "error" || short}
      >
        <MenuItem value=""><em>— pick batch —</em></MenuItem>
        {batches.map(b => {
          const d = daysToExpiry(b.expiryDate);
          const expired = d !== null && d < 0;
          return (
            <MenuItem key={b.batchId} value={b.batchId}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                <span>{b.batchNo}</span>
                <Typography variant="caption"
                  color={expired ? "error" : d !== null && d <= NEAR_EXPIRY_DAYS ? "warning.main" : "text.secondary"}>
                  {expired ? "EXPIRED" : expiryLabel(b.expiryDate)}
                </Typography>
                <Typography variant="caption" color="text.secondary">· {b.availableQty} avail</Typography>
              </Stack>
            </MenuItem>
          );
        })}
      </TextField>
      {selected && (
        <Typography variant="caption" color={exp.color}>{exp.label}</Typography>
      )}
      {short && (
        <Typography variant="caption" color="error">
          Only {selected!.availableQty} available in this batch
        </Typography>
      )}
      {selected && !dispensable(selected) && (
        <Typography variant="caption" color="error">
          This batch cannot be issued — pick another.
        </Typography>
      )}
    </Stack>
  );
}
